import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table.jsx';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.jsx';
import { Badge } from '../../components/ui/badge.jsx';
import { Button } from '../../components/ui/button.jsx';
import { Input } from '../../components/ui/input.jsx';
import { MagnifyingGlassIcon, PlusIcon } from '@radix-ui/react-icons';
import { useToast } from "../../hooks/use-toast.js";
import axios from '../../lib/axios.js';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select.jsx";
import { useIsUserAdmin } from '../../hooks/authHelpers.js';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import { formatPhoneNumber } from '@/helpers/formatPhoneNumber.js';

// Skeleton row component for loading state
const TableRowSkeleton = () => (
    <TableRow>
        <TableCell>
            <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
            <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
            <div className="h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
            <div className="h-4 w-1/3 bg-gray-200 rounded animate-pulse" />
        </TableCell>
        <TableCell>
            <div className="h-4 w-1/4 bg-gray-200 rounded animate-pulse" />
        </TableCell>
    </TableRow>
);

const CommunitiesPage = () => {
    const isAdmin = useIsUserAdmin();
    const [searchParams, setSearchParams] = useSearchParams();
    const [communities, setCommunities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const { toast } = useToast();
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        total_pages: 0
    });

    const hasCreateCommunity = (isAdmin || pagination?.total < 4) && !isLoading;

    const debouncedFetch = useCallback(
        debounce(async (params) => {
            try {
                setIsLoading(true);
                const response = await axios.get(`/communities?${params}`);
                setCommunities(response.data.data);
                setPagination({
                    total: response.data.pagination.total,
                    page: response.data.pagination.page,
                    limit: response.data.pagination.limit,
                    total_pages: response.data.pagination.total_pages
                });
            } catch (error) {
                console.error('Error fetching communities:', error);
                toast({
                    title: "Error",
                    description: "Failed to load communities. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoading(false);
            }
        }, 300),
        []
    );

    useEffect(() => {
        const params = {
            page: searchParams.get('page') || 1,
            limit: pagination.limit,
            sort_by: searchParams.get('sort_by') || 'name',
            sort_order: searchParams.get('sort_order') || 'asc',
        };

        const statusFilter = searchParams.get('status') || (isAdmin ? 'active' : 'all');
        const searchFilter = searchParams.get('search') || undefined;

        if (statusFilter !== 'all') {
            params.status = statusFilter;
        }

        if (searchFilter) {
            params.search = searchFilter;
        }

        const encodedParams = new URLSearchParams(params).toString();
        debouncedFetch(encodedParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const searchTerm = searchParams.get('search') || '';
    const statusValue = searchParams.get('status') || (isAdmin ? 'active' : 'all');
    const sortValue = `${(searchParams.get('sort_by') || 'name')}_${searchParams.get('sort_order') || 'asc'}`;

    console.log(sortValue);

    const handleStatusFilter = (status) => {
        if ((isAdmin && status !== 'active') || (!isAdmin && status !== 'all')) {
            searchParams.set('status', status);
        } else {
            searchParams.delete('status');
        }
        searchParams.delete('page')
        setSearchParams(searchParams);
    }

    const handleSort = (sort) => {
        if (sort !== 'name_asc') {
            const [sortBy, sortOrder] = sort.split('_');
            searchParams.set('sort_by', sortBy);
            searchParams.set('sort_order', sortOrder);
        } else {
            searchParams.delete('sort_by');
            searchParams.delete('sort_order');
        }
        searchParams.delete('page')
        setSearchParams(searchParams);
    }

    const handleSearchChange = ({ target }) => {
        const { value } = target;

        if (value) {
            searchParams.set('search', value);
        } else {
            searchParams.delete('search');
        }

        searchParams.set('page', 1); // Optional: reset to page 1 on new search
        setSearchParams(searchParams);
    };

    const handlePageChange = (newPage) => {
        if (newPage !== 1) {
            searchParams.set('page', newPage);
        } else {
            searchParams.delete('page');
        }
        setPagination(prev => ({ ...prev, page: newPage }));
        setSearchParams(searchParams);
    };

    const handleCreateCommunity = () => {
        navigate('/communities/create');
    };

    const communityAnswerRate = (community) => {
        if (parseInt(community?.total_outgoing_calls_count) === 0) {
            return 0;
        }
        return (parseInt(community?.total_outgoing_answered_calls_count) / parseInt(community?.total_outgoing_calls_count)) * 100;
    }

    return (
        <div className="min-h-full min-w-full flex flex-col">
            <Card className="w-full max-w-7xl mx-auto">
                <CardHeader>
                    <CardTitle className="text-3xl font-bold text-center">Communities</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 gap-2 mb-4 sm:grid-cols-2 lg:grid-cols-4 items-start">
                        {/* Search input spans full width on mobile, more on larger screens */}
                        <div className="relative col-span-full sm:col-span-1 lg:col-span-3">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                            <Input
                                type="text"
                                placeholder="Search communities..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="pl-9 w-full"
                            />
                        </div>

                        {/* Button aligns to end on larger screens */}
                        <div className="sm:col-span-1 lg:col-span-1 flex justify-end">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className={isAdmin ? "" : "inline-block"}>
                                            <Button
                                                disabled={!hasCreateCommunity}
                                                onClick={handleCreateCommunity}
                                            >
                                                <PlusIcon className="w-4 h-4 mr-2" />
                                                Create Community
                                            </Button>
                                        </div>
                                    </TooltipTrigger>
                                    {!isAdmin && (
                                        <TooltipContent>
                                            <p>Please contact support for creating a community.</p>
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>

                    <div className='flex justify-end mt-5 mb-4'>
                        <div className="sm:flex items-center sm:space-x-2">
                            <Select value={statusValue} onValueChange={handleStatusFilter}>
                                <SelectTrigger>
                                    <div>
                                        <span className='font-bold'>Status:</span> <SelectValue value={isAdmin ? 'active' : 'all'} />
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="deactived">Deactivated</SelectItem>
                                    <SelectItem value="onboarding">Onboarding</SelectItem>
                                    <SelectItem value="secret-shopper">Secret Shopper</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={sortValue} onValueChange={handleSort}>
                                <SelectTrigger>
                                    <div>
                                        <span className='font-bold'>Sort:</span> <SelectValue value="name_asc" />
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="name_asc">name, A-Z</SelectItem>
                                    <SelectItem value="name_desc">name, Z-A</SelectItem>
                                    <SelectItem value="ltar_asc">LTAR, low to high</SelectItem>
                                    <SelectItem value="ltar_desc">LTAR, high to low</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[35%]">Community Name</TableHead>
                                    <TableHead className="w-[15%]">Community Phone</TableHead>
                                    <TableHead className="w-[15%]">Flair Phone</TableHead>
                                    <TableHead className="w-[15%]">Status</TableHead>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <TableHead className="w-[20%]">LTAR % (30 days)</TableHead>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <p className="text-sm">Leasing Team Answer Rate: The percentage of outgoing calls that were answered by the community</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    [...Array(pagination.limit)].map((_, index) => (
                                        <TableRowSkeleton key={index} />
                                    ))
                                ) : communities?.length > 0 ? (
                                    communities?.map((community) => (
                                        <TableRow key={community.id}>
                                            <TableCell className="font-medium">
                                                <Link to={`/communities/${community.id}`} className='underline'>
                                                    {community.community_name}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{formatPhoneNumber(community.community_phone_number)}</TableCell>
                                            <TableCell>{formatPhoneNumber(community.assigned_flair_phone_number)}</TableCell>
                                            <TableCell>
                                                <Badge variant="secondary" className="font-normal">
                                                    {community.status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {parseFloat(communityAnswerRate(community)).toFixed(2)}%
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center py-8">
                                            <Badge variant="secondary">No communities found</Badge>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {!isLoading && pagination.total_pages > 1 && (
                        <div className="flex justify-center mt-6">
                            <Pagination currentPage={pagination.page} lastPage={pagination.total_pages}>
                                <PaginationContent>
                                    <PaginationItem>
                                        <PaginationPrevious
                                            onClick={() => handlePageChange(pagination.page - 1)}
                                        />
                                    </PaginationItem>

                                    {[...Array(pagination.total_pages)].map((_, index) => {
                                        const pageNumber = index + 1;

                                        // Show first page, last page, and 1 page around current page
                                        if (
                                            pageNumber === 1 ||
                                            pageNumber === pagination.total_pages ||
                                            (pageNumber >= pagination.page - 1 && pageNumber <= pagination.page + 1)
                                        ) {
                                            return (
                                                <PaginationItem key={pageNumber}>
                                                    <PaginationLink
                                                        onClick={() => handlePageChange(pageNumber)}
                                                        isActive={pageNumber === pagination.page}
                                                    >
                                                        {pageNumber}
                                                    </PaginationLink>
                                                </PaginationItem>
                                            );
                                        }

                                        // Show ellipsis for gaps
                                        if (
                                            pageNumber === 2 ||
                                            pageNumber === pagination.total_pages - 1
                                        ) {
                                            return (
                                                <PaginationItem key={pageNumber}>
                                                    <PaginationEllipsis />
                                                </PaginationItem>
                                            );
                                        }

                                        return null;
                                    })}

                                    <PaginationItem>
                                        <PaginationNext onClick={() => handlePageChange(pagination.page + 1)} />
                                    </PaginationItem>
                                </PaginationContent>
                            </Pagination>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default CommunitiesPage;